import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, Statistic, Table, Tag, Modal, InputNumber,
  Typography, Space, Form, Input, Tabs, message, Tooltip, Badge,
  Progress, Popconfirm, Empty, Alert, Divider
} from 'antd';
import {
  BankOutlined, WalletOutlined, UserAddOutlined, ReloadOutlined,
  ThunderboltOutlined, RollbackOutlined, SettingOutlined,
  CheckCircleOutlined, WarningOutlined, TeamOutlined, BarChartOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100');
const axiosInst = axios.create({ baseURL: API_BASE });
axiosInst.interceptors.request.use(cfg => {
  const t = localStorage.getItem('token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function SchoolAI() {
  const [balance, setBalance] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rechargeVisible, setRechargeVisible] = useState(false);
  const [allocateVisible, setAllocateVisible] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [recharging, setRecharging] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [rechargeForm] = Form.useForm();
  const [allocateForm] = Form.useForm();

  const fetchBalance = useCallback(async () => {
    try {
      const { data } = await axiosInst.get('/api/v2/school-ai/balance');
      if (data.success) setBalance(data.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInst.get('/api/v2/school-ai/allocations');
      if (data.success) setTeachers(data.data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  const fetchTransactions = useCallback(async (page = 1) => {
    try {
      const { data } = await axiosInst.get(`/api/v2/school-ai/transactions?page=${page}&limit=15`);
      if (data.success) { setTransactions(data.data); setTxTotal(data.total); }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchTeachers();
    fetchTransactions();
  }, [fetchBalance, fetchTeachers, fetchTransactions]);

  const handleRecharge = async () => {
    const values = await rechargeForm.validateFields();
    setRecharging(true);
    try {
      const { data } = await axiosInst.post('/api/v2/school-ai/recharge', values);
      if (data.success) {
        message.success(data.message);
        setRechargeVisible(false);
        rechargeForm.resetFields();
        fetchBalance();
        fetchTransactions();
      } else {
        message.error(data.message);
      }
    } catch (e) {
      message.error('操作失败');
    }
    setRecharging(false);
  };

  const handleAllocate = async () => {
    const values = await allocateForm.validateFields();
    setAllocating(true);
    try {
      const { data } = await axiosInst.post('/api/v2/school-ai/allocate', {
        teacher_id: selectedTeacher.teacher_id,
        teacher_name: selectedTeacher.teacher_name,
        amount: values.amount,
        monthly_limit: values.monthly_limit || 0
      });
      if (data.success) {
        message.success(data.message);
        setAllocateVisible(false);
        allocateForm.resetFields();
        fetchBalance();
        fetchTeachers();
        fetchTransactions();
      } else {
        message.error(data.message);
      }
    } catch (e) {
      message.error('分配失败');
    }
    setAllocating(false);
  };

  const handleReclaim = async (teacher) => {
    const available = Math.max(0, teacher.allocated - teacher.consumed);
    if (available <= 0) { message.warning('该教师无可回收余额'); return; }
    try {
      const { data } = await axiosInst.post('/api/v2/school-ai/reclaim', {
        teacher_id: teacher.teacher_id,
        teacher_name: teacher.teacher_name,
        amount: available
      });
      if (data.success) {
        message.success(data.message);
        fetchBalance();
        fetchTeachers();
        fetchTransactions();
      } else {
        message.error(data.message);
      }
    } catch (e) {
      message.error('回收失败');
    }
  };

  const teacherColumns = [
    {
      title: '教师姓名',
      dataIndex: 'teacher_name',
      render: (v, r) => (
        <Space>
          <Badge status={r.is_active ? 'success' : 'default'} />
          <Text strong={r.allocated > 0}>{v || `教师#${r.teacher_id}`}</Text>
        </Space>
      )
    },
    {
      title: '已分配',
      dataIndex: 'allocated',
      width: 100,
      render: v => <Text style={{ color: '#1890ff' }}>¥{parseFloat(v || 0).toFixed(2)}</Text>
    },
    {
      title: '已消耗',
      dataIndex: 'consumed',
      width: 100,
      render: v => <Text style={{ color: '#ff7a00' }}>¥{parseFloat(v || 0).toFixed(4)}</Text>
    },
    {
      title: '剩余可用',
      width: 160,
      render: (_, r) => {
        const allocated = parseFloat(r.allocated || 0);
        const consumed = parseFloat(r.consumed || 0);
        const remaining = Math.max(0, allocated - consumed);
        const pct = allocated > 0 ? Math.round((consumed / allocated) * 100) : 0;
        return allocated > 0 ? (
          <div style={{ minWidth: 120 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <Text type="secondary">¥{remaining.toFixed(2)} 剩余</Text>
              <Text type="secondary">{pct}%</Text>
            </div>
            <Progress percent={pct} size="small" showInfo={false}
              strokeColor={pct > 80 ? '#ff4d4f' : pct > 60 ? '#faad14' : '#52c41a'} />
          </div>
        ) : <Text type="secondary">未分配</Text>;
      }
    },
    {
      title: '月度上限',
      dataIndex: 'monthly_limit',
      width: 100,
      render: v => parseFloat(v) > 0 ? <Tag>¥{parseFloat(v).toFixed(0)}/月</Tag> : <Tag color="default">不限</Tag>
    },
    {
      title: '操作',
      width: 160,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small" type="primary" icon={<UserAddOutlined />}
            onClick={() => { setSelectedTeacher(r); setAllocateVisible(true); allocateForm.resetFields(); }}
          >
            分配
          </Button>
          {parseFloat(r.allocated) > parseFloat(r.consumed) && (
            <Popconfirm
              title={`回收 ${r.teacher_name} 剩余额度 ¥${Math.max(0, r.allocated - r.consumed).toFixed(2)}？`}
              onConfirm={() => handleReclaim(r)}
              okText="确认回收" cancelText="取消"
            >
              <Button size="small" icon={<RollbackOutlined />} danger>回收</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const txColumns = [
    {
      title: '时间', dataIndex: 'created_at', width: 140,
      render: v => new Date(v).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    {
      title: '类型', dataIndex: 'type', width: 80,
      render: v => ({
        recharge: <Tag color="success">充值</Tag>,
        allocate: <Tag color="processing">分配</Tag>,
        consume: <Tag color="warning">消耗</Tag>,
        reclaim: <Tag color="default">回收</Tag>
      }[v] || <Tag>{v}</Tag>)
    },
    { title: '涉及教师', dataIndex: 'teacher_name', width: 100, render: v => v || '-' },
    { title: '说明', dataIndex: 'description', ellipsis: true },
    {
      title: '金额', dataIndex: 'amount', width: 100,
      render: (v, r) => (
        <Text style={{ color: r.type === 'recharge' || r.type === 'reclaim' ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
          {['recharge', 'reclaim'].includes(r.type) ? '+' : '-'}¥{Math.abs(v).toFixed(2)}
        </Text>
      )
    },
    {
      title: '操作后余额', dataIndex: 'balance_after', width: 110,
      render: v => v != null ? <Text type="secondary">¥{parseFloat(v).toFixed(2)}</Text> : '-'
    }
  ];

  const totalAllocated = teachers.reduce((s, t) => s + parseFloat(t.allocated || 0), 0);
  const totalConsumed = teachers.reduce((s, t) => s + parseFloat(t.consumed || 0), 0);
  const bal = parseFloat(balance?.balance || 0);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <BankOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          学校AI模型设置
        </Title>
        <Text type="secondary">学校统一购买AI算力，按需分配给教师使用，集中管控、灵活调配</Text>
      </div>

      {/* 余额概览 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
            bodyStyle={{ padding: '18px 20px' }}>
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, marginBottom: 4 }}>
              <WalletOutlined /> 学校余额池
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>
              ¥{bal.toFixed(2)}
            </div>
            <Button ghost size="small" style={{ marginTop: 8, borderColor: 'rgba(255,255,255,0.5)', color: '#fff' }}
              icon={<ThunderboltOutlined />}
              onClick={() => { setRechargeVisible(true); rechargeForm.resetFields(); }}>
              充值
            </Button>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card bodyStyle={{ padding: '18px 20px' }}>
            <Statistic title={<span><TeamOutlined style={{ color: '#1890ff', marginRight: 4 }} />已分配教师</span>}
              value={teachers.filter(t => t.allocated > 0).length}
              suffix={`/ ${teachers.length} 位`} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card bodyStyle={{ padding: '18px 20px' }}>
            <Statistic title={<span><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />累计分配</span>}
              value={totalAllocated.toFixed(2)} prefix="¥" valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card bodyStyle={{ padding: '18px 20px' }}>
            <Statistic title={<span><BarChartOutlined style={{ color: '#ff7a00', marginRight: 4 }} />教师已消耗</span>}
              value={totalConsumed.toFixed(4)} prefix="¥" valueStyle={{ color: '#ff7a00' }} />
          </Card>
        </Col>
      </Row>

      {bal === 0 && (
        <Alert
          message="学校余额池为空"
          description="请先为学校充值，再向教师分配AI使用额度。"
          type="warning" showIcon icon={<WarningOutlined />}
          style={{ marginBottom: 20 }}
          action={<Button type="primary" size="small" onClick={() => setRechargeVisible(true)}>立即充值</Button>}
        />
      )}

      <Tabs defaultActiveKey="teachers">
        <TabPane tab={<span><TeamOutlined /> 教师额度管理</span>} key="teachers">
          <Card>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">点击"分配"向教师添加AI使用额度，超出分配额度后教师将无法使用学校AI。</Text>
            </div>
            <Table
              dataSource={teachers}
              columns={teacherColumns}
              rowKey="teacher_id"
              loading={loading}
              size="middle"
              pagination={{ pageSize: 15, showTotal: t => `共 ${t} 位教师` }}
              locale={{ emptyText: <Empty description="暂无教师数据" /> }}
            />
          </Card>
        </TabPane>

        <TabPane tab={<span><ReloadOutlined /> 充值 / 分配记录</span>} key="transactions">
          <Card>
            <Table
              dataSource={transactions}
              columns={txColumns}
              rowKey="id"
              size="middle"
              pagination={{
                total: txTotal, pageSize: 15,
                onChange: p => fetchTransactions(p),
                showTotal: t => `共 ${t} 条`
              }}
              locale={{ emptyText: <Empty description="暂无记录" /> }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 学校充值弹窗 */}
      <Modal
        title={<span><BankOutlined style={{ marginRight: 8 }} />学校AI余额充值</span>}
        open={rechargeVisible}
        onCancel={() => setRechargeVisible(false)}
        onOk={handleRecharge}
        confirmLoading={recharging}
        okText="确认充值"
        width={440}
      >
        <Alert message="充值后可向学校教师分配AI使用额度，集中管控使用。" type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={rechargeForm} layout="vertical">
          <Form.Item label="充值金额（元）" name="amount"
            rules={[{ required: true, message: '请输入金额' }, { type: 'number', min: 1, max: 100000 }]}>
            <InputNumber style={{ width: '100%' }} prefix="¥" min={1} max={100000} precision={0}
              placeholder="请输入充值金额" size="large" />
          </Form.Item>
          <Form.Item label="支付方式" name="payment_method" initialValue="transfer">
            <Input placeholder="对公转账 / 微信支付 / 支付宝" />
          </Form.Item>
          <Form.Item label="备注" name="notes">
            <Input.TextArea placeholder="可选：充值说明、合同编号等" rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 分配额度弹窗 */}
      <Modal
        title={
          <span>
            <UserAddOutlined style={{ marginRight: 8 }} />
            分配额度 — {selectedTeacher?.teacher_name || ''}
          </span>
        }
        open={allocateVisible}
        onCancel={() => setAllocateVisible(false)}
        onOk={handleAllocate}
        confirmLoading={allocating}
        okText="确认分配"
        width={420}
      >
        {selectedTeacher && (
          <Alert
            message={`当前已分配 ¥${parseFloat(selectedTeacher.allocated || 0).toFixed(2)}，已消耗 ¥${parseFloat(selectedTeacher.consumed || 0).toFixed(4)}`}
            type="info" showIcon style={{ marginBottom: 16 }} />
        )}
        <Alert
          message={`学校余额池剩余 ¥${bal.toFixed(2)}，本次分配将从余额池中扣除。`}
          type="warning" showIcon style={{ marginBottom: 16 }} />
        <Form form={allocateForm} layout="vertical">
          <Form.Item label="分配金额（元）" name="amount"
            rules={[
              { required: true, message: '请输入分配金额' },
              { type: 'number', min: 0.01, max: bal || 0.01,
                message: `不能超过学校余额 ¥${bal.toFixed(2)}` }
            ]}>
            <InputNumber style={{ width: '100%' }} prefix="¥" min={0.01}
              max={bal} precision={2} placeholder="本次分配额度" size="large" />
          </Form.Item>
          <Form.Item label="月度消耗上限（元，0 = 不限）" name="monthly_limit">
            <InputNumber style={{ width: '100%' }} prefix="¥" min={0} precision={0}
              placeholder="0 表示不设上限" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
